import type { CalcStep } from "@/lib/calc/steps";

const NUMERALS: [number, string][] = [[1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"], [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
const VALUES: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
const STRICT = /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;

export function toRoman(n: number): string {
  let out = "", r = n;
  for (const [v, s] of NUMERALS) while (r >= v) { out += s; r -= v; }
  return out;
}

/** Combining overline (U+0305). A letter followed by it counts 1,000 times as much (the vinculum). */
export const OVERLINE = "\u0305";
export const MAX_ROMAN = 3999999;

/** Letters of a numeral with a flag for the ones that carry a bar, for drawing the bar with CSS. */
export function romanGlyphs(s: string): { ch: string; bar: boolean }[] {
  const out: { ch: string; bar: boolean }[] = [];
  for (const ch of Array.from(s)) {
    if ((ch === OVERLINE || ch === "\u0304") && out.length) out[out.length - 1].bar = true;
    else out.push({ ch, bar: false });
  }
  return out;
}

/** 1 to 3,999,999. Above 3,999 the thousands are written with a bar over every letter (V + bar = 5,000). */
export function toRomanVinculum(n: number): string {
  if (n <= 3999) return toRoman(n);
  const thousands = Math.floor(n / 1000), rest = n % 1000;
  return toRoman(thousands).split("").map((c) => c + OVERLINE).join("") + (rest ? toRoman(rest) : "");
}

export interface RomanPart { value: number; symbol: string; bar: boolean; place: string }
const PLACES = ["thousands", "hundreds", "tens", "ones"];
/** The pieces of a numeral by place: 1994 = M (1000) + CM (900) + XC (90) + IV (4). Barred pieces are the thousands above 3,999. */
export function romanParts(n: number): RomanPart[] {
  const parts: RomanPart[] = [];
  const add = (num: number, bar: boolean) => {
    const digits = String(num).padStart(4, "0").split("");
    digits.forEach((d, i) => {
      const v = Number(d) * 10 ** (3 - i);
      if (v > 0) parts.push({ value: v * (bar ? 1000 : 1), symbol: toRoman(v), bar, place: PLACES[i] });
    });
  };
  if (n > 3999) { add(Math.floor(n / 1000), true); if (n % 1000) add(n % 1000, false); } else add(n, false);
  return parts;
}

const fmtN = (n: number) => n.toLocaleString("en-US");
const partText = (p: RomanPart) => (p.bar ? p.symbol.split("").map((c) => c + OVERLINE).join("") : p.symbol);

/** "M + CM + XC + IV" (barred pieces carry the overline). */
export function romanBreakdown(n: number): string {
  return romanParts(n).map(partText).join(" + ");
}

/** The working for a number to numeral conversion, built from the same pieces the result is joined from. */
export function romanSteps(n: number): CalcStep[] {
  const parts = romanParts(n);
  const steps: CalcStep[] = parts.map((p) => ({
    label: `${p.bar ? "Barred " : ""}${p.place}: ${fmtN(p.value)}`,
    formula: p.bar
      ? `${fmtN(p.value / 1000)} thousand is written ${p.symbol}, with a bar over every letter because the bar multiplies by 1,000`
      : `${fmtN(p.value)} is written ${p.symbol}`,
    inputs: { value: fmtN(p.value) },
    result: partText(p),
  }));
  steps.push({
    label: "Join the pieces",
    formula: "Write the pieces from the largest place to the smallest",
    inputs: { pieces: parts.map(partText).join(" + ") },
    result: toRomanVinculum(n),
  });
  return steps;
}

export type NumberToRoman = { roman: string; vinculum: boolean } | { error: string };
/** Whole numbers from 1 to 3,999,999. Commas as thousands separators are accepted (1,994). */
export function numberToRoman(raw: string): NumberToRoman | null {
  let t = raw.trim().replace(/[\s_]/g, "");
  if (!t) return null;
  if (/^\d{1,3}(,\d{3})+$/.test(t)) t = t.replace(/,/g, "");
  if (!/^-?\d+$/.test(t)) return { error: /^-?\d*[.,]\d+$/.test(t) ? "Roman numerals have no decimals. Use a whole number." : `Enter a whole number from 1 to ${fmtN(MAX_ROMAN)}.` };
  const n = Number(t);
  if (n < 1) return { error: "Roman numerals start at 1. There is no symbol for zero and no negative numbers." };
  if (n > MAX_ROMAN) return { error: `Roman numerals here go from 1 to ${fmtN(MAX_ROMAN)}: above 3,999 a bar over the letters multiplies by 1,000, and three barred M's is the limit.` };
  return { roman: toRomanVinculum(n), vinculum: n > 3999 };
}

export type RomanToNumber = { value: number; clock?: boolean; vinculum?: boolean } | { error: string };
/** Strict validation: only properly written numerals are accepted. IIII (clock style) is allowed with a note. */
export function romanToNumber(raw: string): RomanToNumber | null {
  const s0 = raw.trim().toUpperCase();
  if (!s0) return null;
  if (/[\u0305\u0304]/.test(s0) || /^\(/.test(s0)) return vinculumToNumber(s0);
  const s = s0;
  if (!/^[IVXLCDM]+$/.test(s)) return { error: "Use only the letters I, V, X, L, C, D and M." };
  if (STRICT.test(s)) {
    const value = s.split("").reduce((tot, ch, i) => (VALUES[s[i + 1]] > VALUES[ch] ? tot - VALUES[ch] : tot + VALUES[ch]), 0);
    return value >= 1 ? { value } : { error: "Not a valid Roman numeral." };
  }
  // Common clock-face form: IIII for 4 (also inside XIIII etc.).
  if (s.includes("IIII") && STRICT.test(s.replace("IIII", "IV"))) {
    const v = romanToNumber(s.replace("IIII", "IV"));
    if (v && "value" in v) return { value: v.value, clock: true };
  }
  const g = guess(s);
  const why = whyInvalid(s);
  const reason = why ? ` ${why}` : "";
  return { error: g ? `Not a valid Roman numeral.${reason} Did you mean ${toRoman(g)} (${g})? Plain letters go up to MMMCMXCIX (3,999); a bar over a letter multiplies it by 1,000.` : `Not a valid Roman numeral.${reason} Plain letters go up to MMMCMXCIX (3,999); a bar over a letter multiplies it by 1,000.` };
}

const SUBTRACTIVE = new Set(["IV", "IX", "XL", "XC", "CD", "CM"]);
/** The first rule a malformed numeral breaks, in plain words and with the correct form (B-0210). Null when none of these fits. */
export function whyInvalid(s: string): string | null {
  const repeated = /(V{2,}|L{2,}|D{2,})/.exec(s);
  if (repeated) return `${repeated[1]} repeats a letter that is never repeated: V, L and D appear once in a row (write ${repeated[1][0] === "V" ? "X" : repeated[1][0] === "L" ? "C" : "M"} for two of them).`;
  for (let i = 0; i < s.length - 1; i++) {
    const pair = s[i] + s[i + 1];
    if (VALUES[s[i]] < VALUES[s[i + 1]] && !SUBTRACTIVE.has(pair)) {
      return `${pair} is not allowed: only I before V or X, X before L or C, and C before D or M can be subtracted (99 is XCIX, not IC).`;
    }
  }
  const run = /(I{4,}|X{4,}|C{4,})/.exec(s);
  if (run) return `${run[1]} writes one letter ${run[1].length} times in a row; I, X and C are used at most three times in a row, so use the subtractive form (4 is IV, 40 is XL, 400 is CD).`;
  if (/M{4,}/.test(s)) return "Four M in a row would be 4,000; plain letters stop at 3,999, so write thousands above that with a bar.";
  return null;
}

const sumOf = (s: string) => s.split("").reduce((tot, ch, i) => (VALUES[s[i + 1]] > VALUES[ch] ? tot - VALUES[ch] : tot + VALUES[ch]), 0);

/**
 * Numerals with a bar: the barred letters first (typed with a combining overline, or as a bracketed group such as (V)),
 * then plain letters worth less than 1,000. The barred group is read as a numeral of 1 to 3,999 and multiplied by 1,000.
 */
function vinculumToNumber(s: string): RomanToNumber {
  let barred = "", plain = "";
  const paren = /^\(([IVXLCDM]+)\)([IVXLCDM]*)$/.exec(s);
  if (paren) { barred = paren[1]; plain = paren[2]; }
  else {
    let seenPlain = false;
    for (const g of romanGlyphs(s)) {
      if (!/^[IVXLCDM]$/.test(g.ch)) return { error: "Use only the letters I, V, X, L, C, D and M, with a bar (combining overline) or brackets for thousands." };
      if (g.bar) { if (seenPlain) return { error: "Put the barred letters first, then the plain ones: V̅I is 5,001." }; barred += g.ch; }
      else { seenPlain = true; plain += g.ch; }
    }
  }
  if (!barred || !STRICT.test(barred)) return { error: "The barred letters must form a valid numeral from I to MMMCMXCIX (each bar multiplies by 1,000)." };
  if (plain && (!STRICT.test(plain) || sumOf(plain) > 999)) return { error: "After the barred letters, write the rest as a plain numeral below 1,000 (no M)." };
  return { value: sumOf(barred) * 1000 + (plain ? sumOf(plain) : 0), vinculum: true };
}

/** Numeric value of a malformed numeral read left to right (for the "did you mean" hint). */
function guess(s: string): number | null {
  const v = s.split("").reduce((tot, ch, i) => (VALUES[s[i + 1]] > VALUES[ch] ? tot - VALUES[ch] : tot + VALUES[ch]), 0);
  return v >= 1 && v <= 3999 && toRoman(v) !== s ? v : null;
}

export type DateOrder = "dmy" | "mdy";
/** Separators people ask for on tattoos, invitations and certificates. Only these are accepted. */
export const DATE_SEPARATORS = [".", "·", "/", "-", " "] as const;
export type DateSeparator = (typeof DATE_SEPARATORS)[number];

/** True only for real calendar dates (rejects 30 Feb, 31 Apr, and 29 Feb in non-leap years). */
export function isRealDate(day: number, month: number, year: number): boolean {
  if (![day, month, year].every(Number.isInteger) || year < 1 || month < 1 || month > 12 || day < 1) return false;
  const dim = [31, (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= dim[month - 1];
}

/** 10.11.2026 -> X.XI.MMXXVI (day.month.year), or XI.X.MMXXVI with order "mdy"; the separator can be . · / - or a space. Null if not a real date in 1-3999. */
export function dateToRoman(day: number, month: number, year: number, order: DateOrder = "dmy", sep: DateSeparator = "."): string | null {
  if (year > 3999 || !isRealDate(day, month, year)) return null;
  const [a, b] = order === "mdy" ? [month, day] : [day, month];
  const sp = (DATE_SEPARATORS as readonly string[]).includes(sep) ? sep : ".";
  return `${toRoman(a)}${sp}${toRoman(b)}${sp}${toRoman(year)}`;
}

/** Explains why a date cannot be converted, or null when it is fine (or still empty). */
export function dateError(dRaw: string, mRaw: string, yRaw: string): string | null {
  if (![dRaw, mRaw, yRaw].every((v) => v.trim() !== "")) return null;
  const [d, m, y] = [dRaw, mRaw, yRaw].map(Number);
  if (![d, m, y].every(Number.isInteger)) return "Enter whole numbers for day, month and year.";
  if (y < 1 || y > 3999) return "Year must be between 1 and 3999.";
  if (m < 1 || m > 12) return "Month must be between 1 and 12.";
  if (!isRealDate(d, m, y)) return "That date does not exist in the calendar.";
  return null;
}

export interface RomanRow { n: number; roman: string }
/** A run of numbers with their numerals, for the reference tables (1 to 100, years). */
export function romanRange(from: number, to: number): RomanRow[] {
  const out: RomanRow[] = [];
  for (let n = from; n <= to; n++) out.push({ n, roman: toRoman(n) });
  return out;
}
export const REFERENCE_RANGES = [
  { id: "1-100", label: "1 to 100", from: 1, to: 100 },
  { id: "years", label: "Years 1950 to 2050", from: 1950, to: 2050 },
] as const;
